<template>
    <div class="row">
        <div class="d-flex justify-content-between gap-2">
            <label for="input" class="form-label">Please choose a small subset of data to be analyzed.
                (CSV/JSON/AVRO/PARQUET less than 5 MB)</label>
            <div class="d-flex justify-content-end gap-4">
                <InputField :editable="!loading" :label="'PySpark'" type="checkbox" :modelValue="pysparkMode"
                    @change="updatePysparkMode" />
            </div>
        </div>

        <div class="col">
            <BrowseFile v-model="fileData" :acceptedFileTypes="['csv', 'json', 'avro', 'parquet']"
                :maxFileSize="5242880" @input="handleFileDataUpdate" />
        </div>
        <div class="col-2">
            <button class="btn btn-primary w-100" :disabled="!fileData.isFileSelected || loading" @click="submitAMA">
                <div v-if="loading" class="text-center">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <span v-else>Map My Data</span>
            </button>
        </div>
        <div class="col-2"
            v-if="!pysparkMode && response && response.files && $store.state.activeTab == 'Mapping Config'">
            <button class="btn btn-primary w-100"
                @click="showModal({ component: 'addJob', data: { files: configFiles, screenlarge: true } })">
                <span>Create Job</span>
            </button>
        </div>
    </div>
    <div class="row mt-3 mb-3">
        <InputField :editable="!loading" :label="'Custom Instructions'" type="textarea" v-model="custom_instructions"
            :toolTip="'Custom instructions for the mapping process'" />
    </div>
    <tabs :tabs="tabItems" :btnTabs="true" v-if="submitted">
        <template v-slot:audit>
            <div class="mb-3">
                <div v-if="auditLoading" class="card card-body">
                    <div class="d-flex justify-content-center">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
                <div v-else-if="auditError" :class="['alert', `alert-${auditErrorClass}`]">
                    {{ auditError }}
                </div>
                <div v-else class="card">
                    <div class='card-body markdown-content'>
                        <Markdown :content="auditReport" />
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:mapper>
            <div id="">
                <div v-if="mapperError" class="alert alert-danger mb-3">
                    {{ mapperError }}
                </div>
                <div class="row">
                    <div class="col-4">
                        <div class="card">
                            <div class='card-body'>
                                <div v-if="!textResponse && !mapperError">
                                    <p v-if="showCreateJobButton">Creating Mapping Configuraton... </p>
                                    <p v-else>Creating PySpark Configuraton... </p>
                                    <div class="loading-content-card">
                                        <div class="content">
                                            <h2></h2>
                                            <p></p>
                                        </div>
                                    </div>
                                </div>
                                <Markdown :content="textResponse" />
                            </div>
                        </div>
                    </div>
                    <div class="col-8">
                        <div v-if="!response || !response.files" class="mt-3">
                            <p v-if="showCreateJobButton">Creating Mapping Configuraton... </p>
                            <p v-else>Creating PySpark Configuraton... </p>
                            <div class="loading-content-card">
                                <div class="content">
                                    <h2></h2>
                                    <p></p>
                                </div>
                            </div>
                        </div>
                        <job-editor v-model="yamlContent" :activeTab="activeTab"></job-editor>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:advisor>
            <div id="">
                <div v-if="advisorError" class="alert alert-danger mb-3">
                    {{ advisorError }}
                </div>
                <div v-else-if="!advisorResponse" class="card card-body">
                    <p>Analyzing Data Mart Solutions... </p>
                    <div class="loading-content-card">
                        <div class="content">
                            <h2></h2>
                            <p></p>
                        </div>
                    </div>
                </div>
                <div v-else class="card">
                    <div class='card-body markdown-content'>
                        <Markdown :content="advisorResponse"></Markdown>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:graph>
            <div id="">
                <div class="card">
                    <div class='card-body markdown-content'>
                        <div ref="graphContainer" class="graph-container d-flex justify-content-center"></div>
                    </div>
                </div>
            </div>
        </template>
    </tabs>
</template>

<script>
import axios from "axios";
import { nextTick } from "vue";
import untruncateJson from "untruncate-json";
import JobEditor from "../../components/sandbox/JobEditor.vue";
import Tabs from "../../components/Tabs.vue";
import InputField from "../../components/InputField.vue";
import Markdown from "../../components/Markdown.vue";
import BrowseFile from "../../components/BrowseFile.vue";
import * as d3 from "d3";
import { mapActions, mapState } from "vuex";
import { v4 as uuidv4 } from 'uuid';
export default {
    components: { JobEditor, Tabs, Markdown, InputField, BrowseFile },
    data() {
        return {
            submitted: false,
            fileContent: null,
            loading: false,
            auditLoading: false,
            auditError: "",
            auditErrorClass: "danger",
            mapperLoading: false,
            mapperError: "",
            advisorLoading: false,
            advisorError: "",
            activeTab: "",
            response: "",
            eventSource: null,
            isCSV: true,
            auditReport: "",
            advisorResponse: "",
            graphResponse: "",
            configFiles: [],
            pysparkMode: false,
            showCreateJobButton: true,
            fileType: "",
            fileData: {
                s3Path: '',
                presignedUrl: null,
                sandbox: null,
                localFile: null,
                fileContent: null,
                pathError: '',
                isFileSelected: false
            },
            outputParserData: null,
            custom_instructions: "",
            mapperMessageId: null,
            resourceId: null,
        }
    },
    watch: {
        "response.files": {
            handler: function (newFiles) {
                //change key from name to relative_path
                if (newFiles && newFiles.length > 0) {
                    this.configFiles = newFiles.map((file) => {
                        return {
                            relative_path: file.name,
                            content: file.content
                        }
                    });
                } else {
                    this.configFiles = [];
                }
            },
            deep: true
        }
    },
    computed: {
        ...mapState({
            currentClient: state => state.currentClient,
            accessToken: state => state.accessToken,
        }),
        textResponse() {
            if (this.response?.text_response)
                return (this.response.text_response || this.response.textResponse)
            return null
        },
        yamlContent() {
            var self = this
            const files = {}
            if (this.response?.files) {
                for (const file of this.response.files) {
                    files[file.name] = file.content ? file.content : ""
                }
            }
            return files
        },
        tabItems() {
            let tabs = [
                {
                    name: "Audit",
                    id: "id1",
                    slot: "audit",
                    hide: this.isCSV ? false : true,
                    loading: this.auditLoading,
                },
                {
                    name: "Mapping Config",
                    id: "id2",
                    slot: "mapper",
                    loading: this.mapperLoading,
                },
                {
                    name: "Advisor",
                    id: "id3",
                    slot: "advisor",
                    loading: this.advisorLoading,
                },
            ]
            if (this.graphResponse) {
                var self = this
                tabs.push({
                    name: "Graph",
                    id: "id4",
                    slot: "graph",
                    onClick: () => {
                        nextTick().then(() => {
                            self.drawGraph(self.graphResponse)
                        })
                    }
                })
            }


            return tabs
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            fetchOutputParser: "amdp/fetchOutputParser",
            createChat: "amdp/createChat",
            updateChatTitle: "amdp/updateChatTitle",
            upsertChatMessage: "amdp/upsertChatMessage",
        }),
        untruncateJson,
        async submitAMA() {
            this.loading = true;
            this.submitted = true;
            this.auditLoading = true;
            this.mapperLoading = true;
            this.advisorLoading = true;
            // Clear all error states
            this.auditError = "";
            this.mapperError = "";
            this.advisorError = "";
            this.response = "";
            this.auditReport = "";
            this.advisorResponse = "";
            this.outputParserData = null;
            this.mapperMessageId = null;
            this.resourceId = `chat_${uuidv4()}`; // new resourceId for the chat

            // create new chat
            await this.createChat({ 
                feature: "map_my_data",
                create_chat_payload: {
                    resourceId: this.resourceId,
                    cid: this.currentClient.cid,
                }
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create chat:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating new chat: ${err}`,
                });
            });

            // handle create job button visibility
            this.showCreateJobButton = !this.pysparkMode

            // handle base64 encode for avro/parquet files
            if (this.fileType == "avro" || this.fileType == "parquet") {
                let payload = { limit: 10000 }; // fetch at least the 1st 10K rows
                if (this.fileData.localFile) {
                    const base64Data = await this.convertFileToBase64(this.fileData.localFile)
                    this.fileData.fileContent = base64Data;
                    payload.file_content = base64Data;
                    payload.file_type = this.fileType;
                } else {
                    payload.cid = this.$store.state.currentClient.cid;
                    payload.sandbox_name = this.fileData.sandbox;
                    payload.path = this.fileData.s3Path;
                }
                await this.fetchOutputParser(payload).then((response) => {
                    this.outputParserData = response.data ? response.data : [];
                }).catch(error => {
                    let err = error.response ? error.response.data.error : error.message;
                    let toastoptions = {
                        message: err || "Something went wrong while parsing avro/parquet file. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch("addToast", toastoptions);
                    return;
                })
            }

            await this.executeAudit()
            await this.executeMapData()
            await this.executeDataAdvisor()
            this.loading = false;
        },
        async executeAudit() {
            this.auditLoading = true;
            this.auditReport = null;
            this.auditError = ""; // Clear any previous errors
            const endpoint = `${this.$store.state.surfaceUrl}/v1/genai/data_audit`;

            try {
                let payload = {
                    security_phrase: "strawberry123",
                    cid: "test",
                    file_type: this.fileType
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.content = this.fileData.fileContent
                }

                // replace parsed output parser data for processing avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.content = JSON.stringify(this.outputParserData)
                }

                const response = await axios.post(endpoint, payload, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    }
                });

                if (response.data?.markdown_report) {
                    this.auditReport = response.data.markdown_report;
                    console.log(this.auditReport);
                }
            } catch (error) {
                console.error("Error executing audit:", error);

                let err = error.response?.data.error ? error.response.data.error : error.message;
                if (error.response?.status === 401) {
                    this.auditError = "Authentication failed. Please check your credentials and try again.";
                } else if (error.response?.status === 422) {
                    this.auditError = `Failed to execute audit.\nError: ${err}`;
                    this.auditErrorClass = "warning"
                } else {
                    this.auditError = `Error: ${err || "Failed to execute audit"}`;
                }
            } finally {
                this.auditLoading = false;
            }
        },
        async executeMapData() {
            this.response = null;
            this.activeTab = "";
            this.mapperError = ""; // Clear any previous errors
            let tempActiveTab = "";
            const self = this;

            if (this.eventSource) {
                this.eventSource.close();
            }

            // create user message to chat
            let user_message = {
                "role": "user",
                "content": {
                    "audit_report": this.auditReport,
                    "file_type": this.fileType,
                    "file_content": this.fileData.fileContent,
                    "custom_instructions": this.custom_instructions.trim(),
                    "mode": this.pysparkMode ? "pyspark" : "mapper",
                },
                "timestamp": new Date().toISOString(),
            }
            await this.upsertChatMessage({
                feature: "map_my_data",
                resourceId: this.resourceId,
                message_payload: {
                    "messages": [user_message]
                },
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create user message:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating user message: ${err}`,
                });
            });

            try {
                let endpoint = `${this.$store.state.surfaceUrl}/v1/genai/map_my_data`;
                if (this.pysparkMode) endpoint += "?mode=pyspark"

                let payload = {
                    aqfer_portal: true,
                    cid: this.$store.state.currentClient.cid,
                    audit_report: this.auditReport,
                    file_type: this.fileType,
                    custom_instructions: this.custom_instructions.trim()
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.message = this.fileData.fileContent
                }

                // send parsed output parser data for avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.parsed_message = this.outputParserData
                }

                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    },
                    body: JSON.stringify(payload),
                });

                // Check for 401 status code
                if (response.status === 401) {
                    this.mapperError = "Authentication failed. Please check your credentials and try again.";
                    throw new Error("Authentication failed (401)");
                }

                // Check for other error status codes
                if (!response.ok) {
                    this.mapperError = `Server returned error: ${response.status} ${response.statusText}`;
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let loop = true;

                while (loop) {
                    const { value, done } = await reader.read();
                    if (done) {
                        console.log("Stream complete");
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(this.untruncateJson(line.slice(6)));
                                // console.log("Received chunk: ", data); // Debug log

                                // Only update response if we have actual content
                                if (data && (data.text_response || data.files)) {
                                    this.response = data;

                                    // active tab name needs to be fully tokenized before it is set
                                    if (!this.activeTab && data.files && data.files[0].name) {
                                        if (tempActiveTab == data.files[0].name)
                                            this.activeTab = tempActiveTab;
                                        else
                                            tempActiveTab = data.files[0].name;
                                    }
                                }
                            } catch (e) {
                                console.error('Error parsing mapper SSE data:', e);
                            }
                        }
                    }
                }

                // update chat title
                await this.updateChatTitle({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [user_message],
                    },
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to update chat title:', err);
                });

                // create agent message to chat
                let agent_message = {
                    "role": "assistant",
                    "content": this.response,
                    "timestamp": new Date().toISOString(),
                }
                await this.upsertChatMessage({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                }).then((response) => {
                    console.log("Created agent message:", response.data);
                    const created_messages = response.data;
                    this.mapperMessageId = created_messages && created_messages.length > 0 ? created_messages[0].messageId : null;
                }).catch((error) => {
                    console.log("Created agent message(error):", error);
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to create agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error creating agent message: ${err}`,
                    });
                });

            } catch (error) {
                console.error("Error in SSE connection:", error.response?.data?.error || error.message);
                // Only set error if not already set (like for 401 errors)
                if (!this.mapperError) {
                    this.mapperError = error.message || "An error occurred during mapping execution";
                }
            } finally {
                console.log("Final response state:", this.response); // Debug log
                this.mapperLoading = false;
            }
        },
        async executeDataAdvisor() {
            var endpoint = `${this.$store.state.surfaceUrl}/v1/genai/map_my_data/data_advisor`;
            this.advisorError = ""; // Clear any previous errors
            const self = this;

            try {
                let payload = {
                    data_audit: this.auditReport, // Assuming auditReport is available
                    text_response: this.textResponse, // Assuming textResponse is available
                    file_type: this.fileType,
                    custom_instructions: this.custom_instructions.trim()
                }
                if (this.fileData.s3Path && this.fileData.presignedUrl) {
                    // payload.presigned_url = this.fileData.presignedUrl
                    payload.presigned_url = this.fileData.presignedUrl.endsWith("\n") ? this.fileData.presignedUrl.slice(0, -1) : this.fileData.presignedUrl;
                } else {
                    payload.message = this.fileData.fileContent
                }

                // send parsed output parser data for avro/parquet files
                if (this.fileType == "avro" || this.fileType == "parquet") {
                    payload.parsed_message = this.outputParserData
                }

                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/event-stream",
                        "Authorization": `Bearer ${this.$store.state.accessToken}`
                    },
                    body: JSON.stringify(payload),
                });

                // Check for 401 status code
                if (response.status === 401) {
                    this.advisorError = "Authentication failed. Please check your credentials and try again.";
                    throw new Error("Authentication failed (401)");
                }

                // Check for other error status codes
                if (!response.ok) {
                    this.advisorError = `Server returned error: ${response.status} ${response.statusText}`;
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                const processText = async ({ done, value }) => {
                    if (done) {
                        console.log("Stream complete");
                        return;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(this.untruncateJson(line.slice(6)));
                                // Update component state with the received data
                                if (data?.advisor_response) {
                                    self.advisorResponse = data.advisor_response
                                }
                                if (data?.graph_response) {
                                    self.graphResponse = data.graph_response;
                                }
                            } catch (e) {
                                console.error('Error parsing advisor SSE data:', e);
                            }
                        }
                    }

                    return reader.read().then(processText);
                };

                await reader.read().then(processText);

                // update agent message with data advisor and graph response
                let agent_response = JSON.parse(JSON.stringify(this.response));
                agent_response.advisor_response = self.advisorResponse;
                agent_response.graph_response = self.graphResponse;
                let agent_message = {
                    "role": "assistant",
                    "content": agent_response,
                    "timestamp": new Date().toISOString(),
                    "messageId": this.mapperMessageId, // messageId of the agent message to be updated
                }
                await this.upsertChatMessage({
                    feature: "map_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                    method: "PUT",
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to update agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error updating agent message: ${err}`,
                    });
                });
            } catch (error) {
                console.error("Error in SSE connection:", error);
                // advisorError is already set for 401 errors
                if (!this.advisorError) {
                    this.advisorError = error.message || "An error occurred during data advisor execution";
                }
            } finally {
                this.advisorLoading = false;
                if (this.graphResponse) {
                    console.log('drawing graph')
                    console.log(this.graphResponse)
                    this.drawGraph(this.graphResponse)
                }
            }
        },

        drawGraph(data) {
            const width = this.$refs.graphContainer.clientWidth;
            const height = 600;

            // Remove old SVG if it exists
            d3.select(this.$refs.graphContainer).select("svg").remove();

            // Create new SVG with zoom/pan support
            const svg = d3.select(this.$refs.graphContainer)
                .append("svg")
                .attr("width", width)
                .attr("height", height)
                .call(
                    d3.zoom()
                        .scaleExtent([0.5, 5]) // Min zoom 50%, max zoom 500%
                        .on("zoom", (event) => {
                            g.attr("transform", event.transform);
                        })
                );

            // Add a group element for panning/zooming
            const g = svg.append("g");
            this.simulation = d3.forceSimulation(data.nodes)
                .force("link", d3.forceLink(data.edges).id(d => d.id).distance(50))
                .force("charge", d3.forceManyBody().strength(-200))
                .force("center", d3.forceCenter(width / 2, height / 2));

            // Create edges (links)
            const link = g.selectAll(".link")
                .data(data.edges)
                .enter()
                .append("line")
                .attr("stroke", "#999")
                .attr("stroke-width", 1.5)
                .attr("stroke-opacity", 0.6);

            // Create node groups
            const node = g.selectAll(".node")
                .data(data.nodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0.3).restart();
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0);
                            d.fx = null;
                            d.fy = null;
                        })
                );

            // Add circles for nodes
            node.append("circle")
                .attr("stroke", "white") // Border color
                .attr("stroke-width", 1.5) // Border thickness
                .attr("r", d => { // Circle radius
                    if (d.type === "prsn") return 15;
                    if (d.type === "org" || d.type === "hh") return 20;
                    return 12;
                })
                .attr("fill", d => { // Circle colour
                    if (d.type === "prsn") return "blue";
                    if (d.type === "ck") return "orange";
                    if (d.type === "statid") return "green";
                    if (d.type === "ip") return "purple";
                    if (d.type === "org" || d.type === "hh") return "red";
                    return "gray";
                });

            // Add icons inside the circles
            node.append("text")
                .attr("class", "fa")  // Font Awesome class for styling
                .attr("font-family", "FontAwesome")
                .attr("text-anchor", "middle")

                //.attr("x", d => (d.type === "prsn" || d.type === "cookie" || d.type === "statid" ? -6 : -4)) // Adjust horizontal position
                .attr("y", 5)  // Adjust vertical position
                .attr("x", 0)
                .attr("fill", "white")
                .attr("font-size", d => (d.type === "prsn" ? "14px" : d.type === "org" ? "18px" : d.type === "statid" ? "11px" : "13px"))
                .text(d => {
                    if (d.type === "prsn") return "\uf007"; // person_icon
                    if (d.type === "ck") return "\uf564"; // cookie icon
                    if (d.type === "statid") return "\uf390"; // statid icon
                    if (d.type === "ip") return "\uf6ff"; // network icon
                    if (d.type === "hh") return "\uf015"; // house icon
                    if (d.type === "org") return "\ue533"; // org icon
                    return "\u003f"; // unknown icon
                });

            // Tooltip for nodes
            node.on("mouseover", (event, d) => {
                const tooltip = document.createElement("div");
                tooltip.innerHTML = `<strong>Type: ${d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}`;
                tooltip.className = "graph-tooltip";
                tooltip.style.left = event.pageX + 5 + "px";
                tooltip.style.top = event.pageY + 5 + "px";
                document.body.appendChild(tooltip);
            });
            node.on("mouseout", () => {
                document.querySelectorAll(".graph-tooltip").forEach(tooltip => tooltip.remove());
            });

            // Update positions on simulation tick
            this.simulation.on("tick", () => {
                link
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });
        },
        handleFileDataUpdate(newFileData) {
            this.fileData = newFileData;
            this.fileType = this.getFileType(newFileData)
        },
        convertFileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    // reader.result looks like: "data:application/octet-stream;base64,AAAABBBBCCCC..."
                    const base64 = reader.result.split(',')[1];
                    resolve(base64); // returns only the Base64 part
                };
                reader.onerror = reject;

                // Reads the file and encodes it as a Base64 data URL
                reader.readAsDataURL(file);
            });
        },
        getFileType(fileData) {
            const fileName = fileData?.localFile ? fileData.localFile.name : fileData?.s3Path ? fileData.s3Path : "";
            return fileName.endsWith(".csv")
                ? "csv"
                : fileName.endsWith(".json")
                    ? "json"
                    : fileName.endsWith(".avro")
                        ? "avro"
                        : fileName.endsWith(".parquet")
                            ? "parquet"
                            : "";
        },
        updatePysparkMode() {
            this.pysparkMode = !this.pysparkMode
        },
    },
    beforeMount() {
        this.$emit("setChat", null);
    },
    beforeUnmount() {
        if (this.eventSource) {
            this.eventSource.close();
        }
    },
    emits: ["setChat"]
};
</script>

<style lang="scss" scoped>
@keyframes shine {
    to {
        background-position-x: -200%;
    }
}

.loading-content-card {
    margin: 0px;
    background: #fff;
    border-radius: 5px;

    .image,
    h2,
    p {
        background: #eee;
        background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }

    .content {
        padding: 15px;
    }

    .image {
        height: 200px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    h2 {
        height: 30px;
    }

    p {
        height: 70px;
    }
}

.markdown-content {
    h2 {
        border-bottom: 1px solid black;
    }

    h3 {
        padding: 10px;
        background-color: #EDF2F9;
    }

    table {

        td,
        th {
            padding: 0.5em;
        }
    }

}

// .dropdown-ellipses {
//     color: #6E84A3 !important;
//     font-size: 1.1625rem !important;
// }

// .dropdown-ellipses:hover {
//     color: #000000 !important;
// }

.form-group {
    margin-bottom: 0rem;
}

::v-deep(.form-check-input) {
    background-color: #93bdf0 !important;

    &:checked {
        background-color: #007bff !important;
    }
}

.graph-tooltip {
    position: absolute;
    background-color: #6E84A3;
    color: #fff;
    padding: 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
}
</style>